import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { ListChecks } from 'lucide-react'

import * as Mutation from '../../../api/gql/mutation'
import * as Query from '../../../api/gql/query'
import MeetingFileUpload from './MeetingFileUpload'
import MeetingFileRow, { MeetingFile } from './MeetingFileRow'

interface Props {
  meetingId: string
  files: MeetingFile[]
  agendaItems: Array<{ id: string; title: string; files?: MeetingFile[] }>
  canEdit: boolean
}

const MeetingAttachmentsPanel: React.FC<Props> = ({
  meetingId,
  files,
  agendaItems,
  canEdit,
}) => {
  const { t } = useTranslation()
  const { data: meData } = useQuery(Query.ME)
  const projectId = meData?.me?.currentProject?.id

  const [setMeetingFiles] = useMutation(Mutation.SET_MEETING_FILES)

  const handleFilesChange = useCallback(
    (fileIds: string[]) => setMeetingFiles({ variables: { id: meetingId, fileIds } }),
    [meetingId, setMeetingFiles],
  )

  const agendaGroups = agendaItems
    .map((a) => ({ ...a, files: a.files || [] }))
    .filter((a) => a.files.length > 0)

  const isEmpty = files.length === 0 && agendaGroups.length === 0

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('Meeting_attachments_meeting_level')}
        </h4>
        <MeetingFileUpload
          meetingId={meetingId}
          projectId={projectId}
          files={files}
          onFilesChange={handleFilesChange}
          disabled={!canEdit}
        />
        {files.length === 0 && !canEdit && (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {t('Meeting_files_empty')}
          </p>
        )}
      </div>

      {agendaGroups.map((a) => (
        <div key={a.id} className="space-y-2">
          <h4 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <ListChecks className="h-3.5 w-3.5" />
            <span className="truncate">{a.title}</span>
          </h4>
          <div className="space-y-1">
            {a.files.map((f) => (
              <MeetingFileRow key={f.id} file={f} />
            ))}
          </div>
        </div>
      ))}

      {!isEmpty && (
        <p className="text-xs text-muted-foreground italic">
          {t('Meeting_attachments_agenda_hint')}
        </p>
      )}
    </div>
  )
}

export default MeetingAttachmentsPanel
